import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import API from "../../api/client";
import { Plus, Bell, Search, Camera } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";

export default function TopBar({ title, onCreateTask }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    API.get("/notifications/count").then(r => setUnreadCount(r.data.count)).catch(() => {});
    const interval = setInterval(() => {
      API.get("/notifications/count").then(r => setUnreadCount(r.data.count)).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      navigate(`/tasks?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await API.post("/users/avatar", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Force refresh avatar by adding timestamp
      setAvatarPreview(`${process.env.REACT_APP_BACKEND_URL}${data.avatar_url}?t=${Date.now()}`);
      toast.success("Profile picture updated");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (!file) continue;
        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        try {
          const { data } = await API.post("/users/avatar", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setAvatarPreview(`${process.env.REACT_APP_BACKEND_URL}${data.avatar_url}?t=${Date.now()}`);
          toast.success("Profile picture updated from clipboard");
        } catch {
          toast.error("Upload failed");
        } finally {
          setUploading(false);
        }
        break;
      }
    }
  };

  const initials = user?.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  const displayAvatar = avatarPreview || user?.avatar_url;

  return (
    <div data-testid="topbar" className="h-14 border-b border-[#EBEBEB] flex items-center justify-between px-4 bg-white">
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold tracking-tight font-['Manrope']" data-testid="page-title">
          {title}
        </h1>
      </div>
      <div className="flex items-center gap-2">
        {/* Search */}
        {searchOpen ? (
          <input
            data-testid="topbar-search-input"
            autoFocus
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={handleSearch}
            onBlur={() => { setSearchOpen(false); setSearchQuery(""); }}
            placeholder="Search tasks..."
            className="h-8 w-64 px-3 text-sm border border-[#EBEBEB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4353FF]/20 focus:border-[#4353FF]"
          />
        ) : (
          <button
            data-testid="topbar-search-btn"
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 h-8 px-3 text-sm text-[#686B74] bg-[#F9F9FB] border border-[#EBEBEB] rounded-md hover:bg-[#F4F5F8] transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Search</span>
            <kbd className="hidden sm:inline text-[10px] bg-white border border-[#EBEBEB] px-1 rounded">
              /
            </kbd>
          </button>
        )}

        {/* Create Task */}
        {onCreateTask && (
          <button
            data-testid="topbar-create-task-btn"
            onClick={onCreateTask}
            className="flex items-center gap-1.5 h-8 px-3 text-sm font-medium text-white bg-[#111216] rounded-md hover:bg-[#2B2D31] transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">New Task</span>
          </button>
        )}

        {/* Notifications */}
        <button
          data-testid="topbar-notifications-btn"
          onClick={() => navigate("/notifications")}
          className="relative h-8 w-8 flex items-center justify-center text-[#686B74] hover:bg-[#F4F5F8] rounded-md transition-colors"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 flex items-center justify-center text-[10px] font-medium text-white bg-[#E24A4A] rounded-full">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="topbar-user-menu" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Avatar className="h-7 w-7">
                <AvatarImage src={displayAvatar} alt={user?.name} />
                <AvatarFallback className="text-xs bg-[#111216] text-white">{initials}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-[#686B74]">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="user-menu-profile-pic" onClick={() => setShowProfileDialog(true)}>
              <Camera className="w-3.5 h-3.5 mr-2" /> Change Picture
            </DropdownMenuItem>
            {user?.role === "admin" && (
              <DropdownMenuItem data-testid="user-menu-settings" onClick={() => navigate("/settings")}>
                Settings
              </DropdownMenuItem>
            )}
            <DropdownMenuItem data-testid="user-menu-logout" onClick={logout}>
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Profile Picture Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent data-testid="profile-pic-dialog" className="sm:max-w-sm" onPaste={handlePaste}>
          <DialogHeader>
            <DialogTitle>Update Profile Picture</DialogTitle>
            <p className="text-sm text-[#686B74]">Upload or paste an image to change your photo</p>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <Avatar className="h-24 w-24">
              <AvatarImage src={displayAvatar} alt={user?.name} />
              <AvatarFallback className="text-2xl bg-[#111216] text-white">{initials}</AvatarFallback>
            </Avatar>
            <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
            <div className="flex gap-2">
              <Button
                data-testid="upload-avatar-btn"
                variant="outline"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploading}
              >
                <Camera className="w-3.5 h-3.5 mr-1.5" />
                {uploading ? "Uploading..." : "Upload Photo"}
              </Button>
            </div>
            <p className="text-xs text-[#686B74] text-center">
              Upload an image or paste from clipboard (Ctrl+V)
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
